import Vue from 'vue'

import { httpHelper, languageHelper, trackerHelper } from '@/utils'

const TAGS = {
    beaconPosition: {
        label: 'beaconPosition',
        className: 'list-item__tag orange',
    },
    inactive: { label: 'inactive', className: 'list-item__tag' },
    maintenance: { label: 'maintenance', className: 'list-item__tag' },
}

// Preserve the client-only current_trailer object during a resync.
const CLIENT_OWNED_TRACKER_PATHS = ['current_trailer']

// Preserve newer live fields during a resync.
const LIVE_TRACKER_PATHS = [
    'last_message_timestamp',
    'last_gps_measurement',
    'asset_details.alert',
    'asset_details.position',
    'asset_details.sensor_data',
    'asset_details.current_locations',
]

const state = {
    activeTrackerOnMap: null,
    activeTrackerTrace: [],
    assetHistory: null,
    assetHistoryLocationId: null,
    assetHistoryPoint: null,
    assetHistoryPointProposed: null,
    assetTypes: [],
    selectedAssetTypes: [],
    drivers: [],
    isDriversLoading: false,
    isDriverLoading: false,
    filteredTrackers: null,
    shouldAdjustMapOnFilterChange: false,
    shouldFollowActiveTrackerUpdates: true,
    shouldUpdateTrackers: true,
    trackers: [],
}

const getters = {
    activeInLast6HoursAssets(state) {
        const now = new Date()
        return state.trackers.filter(
            ({ last_message_timestamp }) =>
                now - new Date(last_message_timestamp) < 1000 * 60 * 60 * 6
        )
    },
    alertAssets(state) {
        return state.trackers.filter(tracker => tracker.asset_details.alert)
    },
    assetMeasurementLabels(state) {
        const lang = languageHelper.getUsersLanguage()
        return state.assetTypes.reduce((acc, cur) => {
            Object.entries(cur.measurements?.items || {}).forEach(
                ([key, value]) => {
                    const customKey = `assetMeasurement:${cur.id}:${key}`
                    const label =
                        value.props?.label?.[lang] ||
                        value.props?.label?.[languageHelper.defaultLanguage] ||
                        key
                    acc[customKey] = `${label} (${cur.name})`
                }
            )
            return acc
        }, {})
    },
    assetTags(state) {
        return state.trackers.reduce((acc, cur) => {
            const tags = []

            if (cur.last_gps_measurement?.kind === 'indoor') {
                tags.push(TAGS.beaconPosition)
            }

            if (!cur.active) {
                tags.push(TAGS.inactive)
            }

            if (cur.asset_details.maintenance_due) {
                tags.push(TAGS.maintenance)
            }

            if (tags.length) {
                acc[cur.id] = tags
            }

            return acc
        }, {})
    },
    assetTypeIcons(state) {
        return state.assetTypes.reduce((acc, cur) => {
            acc[cur.id] = cur.marker.url
            return acc
        }, {})
    },
    assetTypesById(state) {
        return state.assetTypes.reduce((acc, cur) => {
            acc[cur.id] = cur
            return acc
        }, {})
    },
    assetsByAssetId(state) {
        return state.trackers.reduce((acc, cur) => {
            acc[cur.asset_details.id] = cur
            return acc
        }, {})
    },
    assetsById(state) {
        return state.trackers.reduce((acc, cur) => {
            acc[cur.id] = cur
            return acc
        }, {})
    },
    assetsByType: state => assets => {
        const trackers = assets
            ? state.trackers.filter(tracker => assets.includes(tracker.asset))
            : state.trackers

        return trackers.reduce((acc, cur) => {
            const assetTypeId = cur.asset_details.asset_type
            acc[assetTypeId] = acc[assetTypeId] ? acc[assetTypeId] + 1 : 1
            return acc
        }, {})
    },
    assetsInLocation: state => locationId => {
        return state.trackers.filter(tracker => tracker.location == locationId)
    },
    customerFieldLabels(state) {
        const lang = languageHelper.getUsersLanguage()
        return state.assetTypes.reduce((acc, cur) => {
            Object.entries(
                cur.additional_data_schema?.properties || {}
            ).forEach(([key, value]) => {
                const customKey = `customerField:${cur.id}:${key}`
                const label =
                    value.label?.[lang] ||
                    value.label?.[languageHelper.defaultLanguage] ||
                    key
                acc[customKey] = `${label} (${cur.name})`
            })
            return acc
        }, {})
    },
    driversByAsset(state) {
        return state.drivers.reduce((acc, cur) => {
            if (cur.last_asset?.id) {
                acc[cur.last_asset.id] = cur
            }
            return acc
        }, {})
    },
    driversById(state) {
        return state.drivers.reduce((acc, cur) => {
            acc[cur.driver_id] = cur
            return acc
        }, {})
    },
    driversByPK(state) {
        return state.drivers.reduce((acc, cur) => {
            acc[cur.id] = cur
            return acc
        }, {})
    },
    fullBinAssets: state => (threshold = 0.8, height = 1.2) => {
        return state.trackers.filter(
            tracker =>
                tracker.asset_details.asset_type_type === 'bin' &&
                height - tracker.asset_details.sensor_data.distance.value >=
                    height * threshold
        )
    },
    isTrackerCountHigh(state) {
        return state.trackers.length >= 500
    },
    trackersFilteredByType(state) {
        return state.selectedAssetTypes?.length
            ? state.trackers.filter(tracker =>
                  state.selectedAssetTypes.some(
                      selectedType =>
                          selectedType.id === tracker.asset_details.asset_type
                  )
              )
            : state.trackers
    },
}

const actions = {
    async loadHistoryData({ commit }, { trackerId, fromDate, toDate }) {
        let url = [
            'measurements/?',
            'tracker=' + trackerId,
            '&timestamp_min=' + encodeURIComponent(fromDate),
            '&timestamp_max=' + encodeURIComponent(toDate),
            '&limit=' + process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
        ].join('')
        let measurements = []
        while (url) {
            const { data } = await httpHelper.get(url)
            measurements = Object.freeze(measurements.concat(data.results))
            url = data.next
        }
        commit('setAssetHistory', measurements)
    },
    async loadDrivers({ commit }) {
        commit('setDriversLoading', true)
        const { data } = await httpHelper.getRecursively(
            '/tachograph/drivers/',
            {
                params: {
                    limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
                },
            }
        )
        commit('setDrivers', data.results)
        commit('setDriversLoading', false)
    },
    async loadDriver({ commit }, driverId) {
        commit('setDriverLoading', true)
        const { data } = await httpHelper.get(
            `/tachograph/driver-details/${driverId}`
        )
        commit('setDriverById', { id: data.driver_id, data })
        commit('setDriverLoading', false)
    },
    batchUpdateTrackerLive({ state, getters }, updates) {
        const updatedIndexes = new Set()

        updates.forEach(({ tracker: id, ...data }) => {
            const index = state.trackers.findIndex(tracker => tracker.id === id)
            const tracker = state.trackers[index]

            if (!tracker) {
                return
            }

            tracker.asset_details.alert = data.alert
            tracker.last_message_timestamp = data.timestamp

            if ('current_locations' in data) {
                tracker.asset_details.current_locations = data.current_locations
            }

            if ('lat' in data && 'long' in data) {
                tracker.asset_details.position.latitude = data.lat
                tracker.asset_details.position.longitude = data.long
                tracker.last_gps_measurement = {
                    ...tracker.last_gps_measurement,
                    lat: data.lat,
                    lng: data.long,
                    timestamp: data.timestamp,
                    ...('heading' in data && { heading: data.heading }),
                    ...('gps_accuracy' in data && {
                        gps_accuracy: data.gps_accuracy,
                    }),
                    ...('fix_time' in data && { fix_time: data.fix_time }),
                }

                if (state.activeTrackerOnMap?.id === id) {
                    state.activeTrackerTrace = [
                        [data.lat, data.long],
                        ...state.activeTrackerTrace,
                    ]
                }
            }

            if ('values' in data) {
                Object.entries(data.values).forEach(([key, value]) => {
                    tracker.asset_details.sensor_data[key] = {
                        value,
                        last_update: data.timestamp,
                    }
                })
            }
            if ('current_trailer' in data && data.current_trailer) {
                tracker.current_trailer =
                    getters.assetsByAssetId[data.current_trailer]?.asset_details
            }
            updatedIndexes.add(index)
        })

        updatedIndexes.forEach(index => {
            Vue.set(state.trackers, index, state.trackers[index])
        })
    },
    // Restore assets after a websocket gap.
    async resyncTrackers({ state, commit, dispatch }) {
        const { data } = await httpHelper.getRecursively('/trackers/', {
            params: {
                limit: process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST,
            },
        })

        const {
            hasChanged,
            hasMembershipChanged,
        } = trackerHelper.mergeTrackersInPlace(state.trackers, data.results, {
            timestampField: 'last_message_timestamp',
            livePaths: LIVE_TRACKER_PATHS,
            clientOwnedPaths: CLIENT_OWNED_TRACKER_PATHS,
        })

        if (!hasChanged && !hasMembershipChanged) {
            return
        }

        const activeId = state.activeTrackerOnMap?.id

        if (activeId && !state.trackers.some(({ id }) => id === activeId)) {
            commit('setActiveTrackerOnMap', null)
        }

        // Rebuild filtered assets when tracker membership or data changes.
        await dispatch('resetFilteredTrackers')
    },
    resetFilteredTrackers({ state, commit, rootState }, filters) {
        commit('setFilteredTrackers', null)
        const assetListFilterPreferences =
            filters || rootState.preferences.assetListFilterPreferences
        if (rootState.preferences.isAssetFilterActive) {
            commit(
                'setFilteredTrackers',
                trackerHelper.filterTrackers(
                    state.trackers,
                    assetListFilterPreferences
                )
            )
        }
    },
}

const mutations = {
    resetAssetHistory(state) {
        state.assetHistory = null
    },
    setActiveTrackerOnMap(state, data) {
        if (!data) {
            state.activeTrackerTrace = []
        } else if (data.id !== state.activeTrackerOnMap?.id) {
            const { latitude, longitude } = data.asset_details.position
            state.activeTrackerTrace = [[latitude, longitude]]
        }

        state.activeTrackerOnMap = data
    },
    setAssetHistory(state, data) {
        state.assetHistory = Object.freeze(data)
    },
    setAssetHistoryLocationId(state, data) {
        state.assetHistoryLocationId = data
    },
    setAssetHistoryPoint(state, data) {
        state.assetHistoryPoint = data
    },
    setAssetHistoryPointProposed(state, data) {
        state.assetHistoryPointProposed = data
    },
    setDrivers(state, data) {
        state.drivers = data
    },
    setDriverLoading(state, data) {
        state.isDriverLoading = data
    },
    setDriversLoading(state, data) {
        state.isDriversLoading = data
    },
    setFilteredTrackers(state, data) {
        state.filteredTrackers = data
    },
    setSelectedAssetTypes(state, data) {
        state.selectedAssetTypes = data
    },
    setShouldAdjustMapOnFilterChange(state, data) {
        state.shouldAdjustMapOnFilterChange = data
    },
    setShouldFollowActiveTrackerUpdates(state, data) {
        state.shouldFollowActiveTrackerUpdates = data
    },
    switchShouldFollowActiveTrackerUpdates(state) {
        state.shouldFollowActiveTrackerUpdates = !state.shouldFollowActiveTrackerUpdates
    },
    updateAsset(state, { id, ...data }) {
        const index = state.trackers.findIndex(tracker => tracker.asset === id)
        const tracker = state.trackers[index]

        if (!tracker) {
            return
        }

        Object.keys(data).forEach(key => {
            tracker.asset_details[key] = data[key]
        })

        Vue.set(state.trackers, index, tracker)
    },
    updateTracker(state, { id, ...data }) {
        const index = state.trackers.findIndex(tracker => tracker.id === id)
        const tracker = state.trackers[index]

        if (!tracker) {
            return
        }

        Object.keys(data).forEach(key => {
            tracker[key] = data[key]
        })

        Vue.set(state.trackers, index, tracker)
    },
    setDriverById(state, payload) {
        const index = state.drivers.findIndex(
            driver => driver.driver_id === payload.id
        )
        if (index !== -1) {
            Vue.set(state.drivers, index, payload.data)
        } else {
            state.drivers.push(payload.data)
        }
    },
    setShouldUpdateTrackers(state, data) {
        state.shouldUpdateTrackers = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
